import type { Expert } from '../types/expert';

export const experts: Expert[] = [
  {
    id: 'mariam-al-mansoori',
    name: 'Dr. Mariam Al Mansoori',
    title: 'AI Policy & Digital Government Advisor',
    organization: 'Khalifa University',
    location: 'Abu Dhabi',
    nationality: 'UAE',
    isEmirati: true,
    languages: ['Arabic', 'English'],
    bio: 'Advises federal entities on responsible AI adoption and digital service design. Former lead on national AI readiness assessments with 15+ years in public-sector transformation.',
    dailyRate: 18000,
    halfDayRate: 10000,
    availability: 'Available',
    memberSince: '2021-03-12',
    expertise: [
      { name: 'Artificial Intelligence Policy', confidence: 97, source: 'Verified' },
      { name: 'Digital Government', confidence: 94, source: 'Verified' },
      { name: 'Data Governance', confidence: 86, source: 'Verified' },
      { name: 'Smart Cities', confidence: 78, source: 'AI suggested' },
      { name: 'Education Technology', confidence: 64, source: 'AI suggested' },
    ],
    reviews: [
      { author: 'Hamad Al Shamsi', organization: 'TDRA', rating: 5, comment: 'Exceptionally clear framing of AI risk for senior leadership. Our board asked for a follow-up session.', date: '2026-09-18' },
      { author: 'Sara Al Hosani', organization: 'Abu Dhabi Digital Authority', rating: 5, comment: 'Brought practical, UAE-specific examples. Workshop outputs went straight into our roadmap.', date: '2026-06-02' },
      { author: 'Michael Chen', organization: 'GITEX Global', rating: 4, comment: 'Strong keynote, well received. Slightly over time on Q&A.', date: '2025-10-16' },
    ],
    events: [
      { id: 'e1', name: 'National AI Readiness Workshop', organizer: 'TDRA', date: '2026-09-15', role: 'Lead facilitator', location: 'Abu Dhabi', fee: 36000, status: 'Paid', rating: 5 },
      { id: 'e2', name: 'Government Services Summit', organizer: 'Prime Minister’s Office', date: '2026-07-22', role: 'Panelist', location: 'Dubai', fee: 10000, status: 'Pending', rating: 5 },
      { id: 'e3', name: 'Data Strategy Advisory Sprint', organizer: 'Abu Dhabi Digital Authority', date: '2026-05-28', role: 'Advisor', location: 'Abu Dhabi', fee: 54000, status: 'Paid', rating: 5 },
      { id: 'e4', name: 'GITEX Global — AI Stage', organizer: 'DWTC', date: '2025-10-14', role: 'Keynote speaker', location: 'Dubai', fee: 18000, status: 'Paid', rating: 4 },
      { id: 'e5', name: 'World Government Summit', organizer: 'WGS Organization', date: '2027-02-10', role: 'Moderator', location: 'Dubai', fee: 18000, status: 'Upcoming' },
    ],
    social: {
      overallScore: 91,
      sentiment: 'Positive',
      summary: 'Highly active thought leader on responsible AI in the region. Posts consistently on LinkedIn with strong professional engagement and is frequently quoted on X during major government events. Content is policy-focused, bilingual, and well aligned with UAE national priorities. No reputational risks detected in the last 12 months.',
      topics: ['Responsible AI', 'UAE AI Strategy 2031', 'Digital services', 'Women in tech'],
      strengths: ['Consistent weekly posting', 'High-quality bilingual content', 'Strong network of government decision makers'],
      watchouts: ['Instagram presence is mostly personal and low-frequency'],
      accounts: [
        { platform: 'LinkedIn', handle: 'mariam-almansoori', url: 'https://linkedin.com', followers: 48200, engagementRate: 4.8, score: 95, postsPerMonth: 9, latestPost: { text: 'Proud to share the outcomes of this week’s National AI Readiness Workshop — 14 entities, one shared framework.', date: '2026-10-24', likes: 1840, comments: 132 } },
        { platform: 'X', handle: '@mariam_ai', url: 'https://x.com', followers: 22600, engagementRate: 2.9, score: 84, postsPerMonth: 22, latestPost: { text: 'Responsible AI isn’t a brake, it’s the steering wheel. #UAEAI', date: '2026-10-26', likes: 640, comments: 48 } },
        { platform: 'Instagram', handle: '@dr.mariam.m', url: 'https://instagram.com', followers: 6100, engagementRate: 3.1, score: 58, postsPerMonth: 2, latestPost: { text: 'Behind the scenes at Khalifa University AI lab.', date: '2026-09-30', likes: 410, comments: 19 } },
      ],
    },
  },
  {
    id: 'khalid-al-suwaidi',
    name: 'Khalid Al Suwaidi',
    title: 'Energy Transition & Sustainability Strategist',
    organization: 'Independent',
    location: 'Dubai',
    nationality: 'UAE',
    isEmirati: true,
    languages: ['Arabic', 'English'],
    bio: 'Former COP28 delivery team member specialising in decarbonisation roadmaps for utilities and industrial clusters across the GCC.',
    dailyRate: 15000,
    halfDayRate: 8500,
    availability: 'Limited',
    memberSince: '2022-01-08',
    expertise: [
      { name: 'Energy Transition', confidence: 96, source: 'Verified' },
      { name: 'Climate Policy', confidence: 91, source: 'Verified' },
      { name: 'ESG Reporting', confidence: 82, source: 'Verified' },
      { name: 'Green Hydrogen', confidence: 74, source: 'AI suggested' },
    ],
    reviews: [
      { author: 'Fatima Al Zaabi', organization: 'MOCCAE', rating: 5, comment: 'Deep technical knowledge combined with diplomatic sensitivity. Ideal for ministerial briefings.', date: '2026-08-11' },
      { author: 'Rashid Al Nuaimi', organization: 'DEWA', rating: 4, comment: 'Excellent content, materials were shared a little late.', date: '2026-03-20' },
    ],
    events: [
      { id: 'e1', name: 'Net Zero 2050 Ministerial Briefing', organizer: 'MOCCAE', date: '2026-08-08', role: 'Briefing lead', location: 'Dubai', fee: 30000, status: 'Paid', rating: 5 },
      { id: 'e2', name: 'Utilities Decarbonisation Roundtable', organizer: 'DEWA', date: '2026-03-18', role: 'Facilitator', location: 'Dubai', fee: 15000, status: 'Paid', rating: 4 },
      { id: 'e3', name: 'Abu Dhabi Sustainability Week', organizer: 'Masdar', date: '2026-01-14', role: 'Panelist', location: 'Abu Dhabi', fee: 8500, status: 'Paid' },
      { id: 'e4', name: 'Industrial Clusters ESG Review', organizer: 'ADNOC', date: '2026-10-05', role: 'Advisor', location: 'Abu Dhabi', fee: 45000, status: 'Pending' },
    ],
    social: {
      overallScore: 78,
      sentiment: 'Positive',
      summary: 'Respected voice on climate action with a focused LinkedIn audience of energy professionals. Activity spikes around major sustainability events. X usage is moderate and mostly reshares. Overall tone is constructive and aligned with UAE climate commitments.',
      topics: ['Net zero', 'COP28 legacy', 'Green hydrogen', 'ESG'],
      strengths: ['Niche, high-value professional audience', 'Credible event-driven commentary'],
      watchouts: ['Posting frequency drops between events', 'Limited original content on X'],
      accounts: [
        { platform: 'LinkedIn', handle: 'khalid-alsuwaidi', url: 'https://linkedin.com', followers: 19400, engagementRate: 3.9, score: 86, postsPerMonth: 5, latestPost: { text: 'Three lessons from the Net Zero briefing: policy certainty, grid readiness, and talent.', date: '2026-10-12', likes: 720, comments: 64 } },
        { platform: 'X', handle: '@k_suwaidi', url: 'https://x.com', followers: 8300, engagementRate: 1.6, score: 62, postsPerMonth: 11, latestPost: { text: 'Great to see momentum on hydrogen corridors in the region.', date: '2026-10-20', likes: 120, comments: 9 } },
      ],
    },
  },
  {
    id: 'priya-raman',
    name: 'Dr. Priya Raman',
    title: 'Healthcare Innovation & Digital Health Lead',
    organization: 'Mediclinic Middle East',
    location: 'Dubai',
    nationality: 'India',
    isEmirati: false,
    languages: ['English', 'Hindi', 'Tamil'],
    bio: 'Physician-turned-innovation leader driving telehealth, clinical AI and patient experience programmes across UAE hospital networks.',
    dailyRate: 12000,
    halfDayRate: 7000,
    availability: 'Available',
    memberSince: '2022-09-19',
    expertise: [
      { name: 'Digital Health', confidence: 95, source: 'Verified' },
      { name: 'Clinical AI', confidence: 88, source: 'Verified' },
      { name: 'Patient Experience', confidence: 84, source: 'Verified' },
      { name: 'Health Policy', confidence: 69, source: 'AI suggested' },
    ],
    reviews: [
      { author: 'Dr. Ahmed Al Hammadi', organization: 'Department of Health', rating: 5, comment: 'Grounded, evidence-based and very engaging with clinicians.', date: '2026-07-04' },
      { author: 'Laila Al Marzouqi', organization: 'Arab Health', rating: 5, comment: 'Our highest-rated session of the day.', date: '2026-01-29' },
      { author: 'Tom Barker', organization: 'Dubai Health', rating: 4, comment: 'Valuable insights, would like more regional data next time.', date: '2025-11-12' },
    ],
    events: [
      { id: 'e1', name: 'Clinical AI Governance Workshop', organizer: 'Department of Health', date: '2026-07-02', role: 'Facilitator', location: 'Abu Dhabi', fee: 24000, status: 'Paid', rating: 5 },
      { id: 'e2', name: 'Arab Health Congress', organizer: 'Informa', date: '2026-01-27', role: 'Keynote speaker', location: 'Dubai', fee: 12000, status: 'Paid', rating: 5 },
      { id: 'e3', name: 'Telehealth Strategy Review', organizer: 'Dubai Health', date: '2025-11-10', role: 'Advisor', location: 'Dubai', fee: 36000, status: 'Paid', rating: 4 },
      { id: 'e4', name: 'Patient Experience Forum', organizer: 'EHS', date: '2026-10-01', role: 'Panelist', location: 'Sharjah', fee: 7000, status: 'Pending' },
      { id: 'e5', name: 'Healthcare AI Symposium', organizer: 'MBRU', date: '2026-12-04', role: 'Speaker', location: 'Dubai', fee: 12000, status: 'Upcoming' },
    ],
    social: {
      overallScore: 83,
      sentiment: 'Positive',
      summary: 'Strong clinician-facing presence with educational content that performs well on LinkedIn and YouTube. Recently started short explainer videos which are driving audience growth. Content is compliant and avoids medical advice to the public.',
      topics: ['Clinical AI', 'Telehealth', 'Patient safety', 'Healthcare workforce'],
      strengths: ['Educational video content', 'Fast-growing audience (+18% in 6 months)'],
      watchouts: ['Occasional commentary on international policy outside UAE context'],
      accounts: [
        { platform: 'LinkedIn', handle: 'drpriyaraman', url: 'https://linkedin.com', followers: 31800, engagementRate: 4.2, score: 89, postsPerMonth: 7, latestPost: { text: 'AI won’t replace clinicians — but clinicians using AI safely will set the standard of care.', date: '2026-10-22', likes: 1210, comments: 97 } },
        { platform: 'YouTube', handle: '@DrPriyaExplains', url: 'https://youtube.com', followers: 12400, engagementRate: 5.6, score: 81, postsPerMonth: 3, latestPost: { text: 'How hospitals validate AI tools — in 6 minutes', date: '2026-10-15', likes: 860, comments: 74 } },
        { platform: 'Instagram', handle: '@drpriya.health', url: 'https://instagram.com', followers: 9800, engagementRate: 3.4, score: 70, postsPerMonth: 6, latestPost: { text: 'Panel day at Arab Health 🩺', date: '2026-10-03', likes: 530, comments: 22 } },
        { platform: 'X', handle: '@priyaraman_md', url: 'https://x.com', followers: 4100, engagementRate: 1.2, score: 52, postsPerMonth: 4, latestPost: { text: 'New paper on sepsis prediction models worth a read.', date: '2026-09-28', likes: 64, comments: 5 } },
      ],
    },
  },
  {
    id: 'james-whitfield',
    name: 'James Whitfield',
    title: 'Financial Regulation & Fintech Advisor',
    organization: 'Whitfield Advisory (ADGM)',
    location: 'Abu Dhabi',
    nationality: 'United Kingdom',
    isEmirati: false,
    languages: ['English'],
    bio: 'Former regulator with 20 years across the FCA and ADGM. Advises on virtual asset frameworks, open finance and regulatory sandboxes.',
    dailyRate: 20000,
    halfDayRate: 11500,
    availability: 'Booked',
    memberSince: '2020-11-02',
    expertise: [
      { name: 'Financial Regulation', confidence: 98, source: 'Verified' },
      { name: 'Virtual Assets', confidence: 90, source: 'Verified' },
      { name: 'Open Finance', confidence: 83, source: 'Verified' },
      { name: 'Anti-Money Laundering', confidence: 76, source: 'AI suggested' },
    ],
    reviews: [
      { author: 'Aisha Al Dhaheri', organization: 'Central Bank of the UAE', rating: 5, comment: 'Unmatched depth on regulatory design. Precise and pragmatic.', date: '2026-05-21' },
      { author: 'Yousef Karam', organization: 'Dubai FinTech Summit', rating: 4, comment: 'Excellent content, could be more accessible for a general audience.', date: '2026-04-30' },
    ],
    events: [
      { id: 'e1', name: 'Virtual Assets Framework Consultation', organizer: 'Central Bank of the UAE', date: '2026-05-19', role: 'Lead advisor', location: 'Abu Dhabi', fee: 80000, status: 'Paid', rating: 5 },
      { id: 'e2', name: 'Dubai FinTech Summit', organizer: 'DIFC', date: '2026-04-28', role: 'Panelist', location: 'Dubai', fee: 11500, status: 'Paid', rating: 4 },
      { id: 'e3', name: 'Open Finance Readiness Review', organizer: 'SCA', date: '2025-12-09', role: 'Advisor', location: 'Abu Dhabi', fee: 60000, status: 'Paid' },
      { id: 'e4', name: 'Regulatory Sandbox Masterclass', organizer: 'ADGM Academy', date: '2026-10-18', role: 'Trainer', location: 'Abu Dhabi', fee: 20000, status: 'Pending' },
    ],
    social: {
      overallScore: 64,
      sentiment: 'Neutral',
      summary: 'Professional but low-frequency social presence. LinkedIn is used mainly to share long-form regulatory commentary, which earns solid engagement from a senior finance audience. No meaningful activity on visual platforms. Profile is credible but not a strong amplifier for event promotion.',
      topics: ['Virtual assets', 'Regulatory sandboxes', 'Open finance'],
      strengths: ['Authoritative long-form analysis', 'Senior, niche audience'],
      watchouts: ['Low posting frequency', 'Limited reach for public-facing campaigns'],
      accounts: [
        { platform: 'LinkedIn', handle: 'jameswhitfield', url: 'https://linkedin.com', followers: 14600, engagementRate: 3.1, score: 76, postsPerMonth: 2, latestPost: { text: 'Notes on the evolving virtual asset regime in the UAE — what firms should prepare for in 2027.', date: '2026-10-09', likes: 410, comments: 58 } },
        { platform: 'X', handle: '@jwhitfield_reg', url: 'https://x.com', followers: 3200, engagementRate: 0.9, score: 41, postsPerMonth: 3, latestPost: { text: 'Worth reading: latest FSB report on stablecoins.', date: '2026-09-17', likes: 22, comments: 2 } },
      ],
    },
  },
  {
    id: 'noura-al-ketbi',
    name: 'Noura Al Ketbi',
    title: 'Cultural Heritage & Creative Economy Curator',
    organization: 'Sharjah Art Foundation',
    location: 'Sharjah',
    nationality: 'UAE',
    isEmirati: true,
    languages: ['Arabic', 'English', 'French'],
    bio: 'Curator and cultural strategist working on Emirati heritage preservation, museum programming and the growth of the national creative economy.',
    dailyRate: 9500,
    halfDayRate: 5500,
    availability: 'Available',
    memberSince: '2023-02-15',
    expertise: [
      { name: 'Cultural Heritage', confidence: 96, source: 'Verified' },
      { name: 'Creative Economy', confidence: 87, source: 'Verified' },
      { name: 'Museum Programming', confidence: 85, source: 'Verified' },
      { name: 'Cultural Diplomacy', confidence: 72, source: 'AI suggested' },
      { name: 'Youth Engagement', confidence: 66, source: 'AI suggested' },
    ],
    reviews: [
      { author: 'Maitha Al Falasi', organization: 'Dubai Culture', rating: 5, comment: 'Warm, inspiring and deeply knowledgeable. Audiences loved her.', date: '2026-04-14' },
      { author: 'Jean Moreau', organization: 'Louvre Abu Dhabi', rating: 5, comment: 'A natural bridge between local heritage and international audiences.', date: '2025-12-02' },
    ],
    events: [
      { id: 'e1', name: 'Emirati Heritage Week Talks', organizer: 'Dubai Culture', date: '2026-04-12', role: 'Host', location: 'Dubai', fee: 19000, status: 'Paid', rating: 5 },
      { id: 'e2', name: 'Museum Futures Conference', organizer: 'Louvre Abu Dhabi', date: '2025-11-30', role: 'Panelist', location: 'Abu Dhabi', fee: 5500, status: 'Paid', rating: 5 },
      { id: 'e3', name: 'Creative Economy Strategy Lab', organizer: 'Ministry of Culture', date: '2026-09-09', role: 'Facilitator', location: 'Dubai', fee: 28500, status: 'Pending' },
      { id: 'e4', name: 'Sharjah Biennial Youth Program', organizer: 'Sharjah Art Foundation', date: '2027-01-20', role: 'Curator', location: 'Sharjah', fee: 19000, status: 'Upcoming' },
    ],
    social: {
      overallScore: 88,
      sentiment: 'Positive',
      summary: 'Visually strong presence with a large, engaged Instagram community passionate about Emirati culture. Bilingual storytelling performs exceptionally well with younger audiences. Ideal amplifier for cultural and public-facing events.',
      topics: ['Emirati heritage', 'Contemporary art', 'Museums', 'Youth & culture'],
      strengths: ['Very high Instagram engagement', 'Strong youth reach', 'Authentic bilingual storytelling'],
      watchouts: ['LinkedIn presence is underdeveloped for B2G audiences'],
      accounts: [
        { platform: 'Instagram', handle: '@noura.alketbi', url: 'https://instagram.com', followers: 86400, engagementRate: 6.4, score: 96, postsPerMonth: 14, latestPost: { text: 'Restoring a 1960s Sharjah doorway — every scratch tells a story.', date: '2026-10-25', likes: 7420, comments: 318 } },
        { platform: 'X', handle: '@nouraketbi', url: 'https://x.com', followers: 12900, engagementRate: 2.4, score: 72, postsPerMonth: 10, latestPost: { text: 'Heritage is not the past, it is a living conversation.', date: '2026-10-19', likes: 380, comments: 27 } },
        { platform: 'LinkedIn', handle: 'noura-alketbi', url: 'https://linkedin.com', followers: 5200, engagementRate: 2.1, score: 61, postsPerMonth: 2, latestPost: { text: 'Excited to facilitate the Creative Economy Strategy Lab.', date: '2026-09-05', likes: 190, comments: 14 } },
      ],
    },
  },
  {
    id: 'omar-haddad',
    name: 'Omar Haddad',
    title: 'Cybersecurity & Critical Infrastructure Specialist',
    organization: 'Haddad Cyber Labs',
    location: 'Dubai',
    nationality: 'Jordan',
    isEmirati: false,
    languages: ['Arabic', 'English'],
    bio: 'Security architect focused on national critical infrastructure, incident response readiness and cyber workforce development.',
    dailyRate: 14000,
    halfDayRate: 8000,
    availability: 'Limited',
    memberSince: '2021-07-30',
    expertise: [
      { name: 'Cybersecurity', confidence: 97, source: 'Verified' },
      { name: 'Critical Infrastructure', confidence: 89, source: 'Verified' },
      { name: 'Incident Response', confidence: 87, source: 'Verified' },
      { name: 'Cyber Workforce Training', confidence: 71, source: 'AI suggested' },
    ],
    reviews: [
      { author: 'Saeed Al Mazrouei', organization: 'UAE Cyber Security Council', rating: 5, comment: 'Ran a realistic, high-pressure simulation. Very valuable for our teams.', date: '2026-06-19' },
      { author: 'Hessa Al Qassimi', organization: 'Etisalat by e&', rating: 4, comment: 'Technically excellent; executive summary could be shorter.', date: '2026-02-08' },
      { author: 'Ravi Menon', organization: 'GISEC', rating: 3, comment: 'Good content but the session started late due to AV setup.', date: '2025-09-04' },
    ],
    events: [
      { id: 'e1', name: 'National Cyber Drill', organizer: 'UAE Cyber Security Council', date: '2026-06-17', role: 'Exercise director', location: 'Abu Dhabi', fee: 42000, status: 'Paid', rating: 5 },
      { id: 'e2', name: 'Telecom Resilience Workshop', organizer: 'e&', date: '2026-02-06', role: 'Facilitator', location: 'Dubai', fee: 14000, status: 'Paid', rating: 4 },
      { id: 'e3', name: 'GISEC Global', organizer: 'DWTC', date: '2025-09-02', role: 'Speaker', location: 'Dubai', fee: 8000, status: 'Paid', rating: 3 },
      { id: 'e4', name: 'SOC Readiness Assessment', organizer: 'Abu Dhabi Police', date: '2026-10-12', role: 'Advisor', location: 'Abu Dhabi', fee: 28000, status: 'Pending' },
    ],
    social: {
      overallScore: 71,
      sentiment: 'Mixed',
      summary: 'Active on X with timely threat-intelligence commentary that attracts a technical audience. LinkedIn content is solid and professional. Some X posts adopt a sharp tone in debates with vendors — not a risk, but worth noting for high-profile government events.',
      topics: ['Threat intelligence', 'OT security', 'Incident response', 'Cyber talent'],
      strengths: ['Timely, technical commentary', 'Strong following among security practitioners'],
      watchouts: ['Occasional combative tone on X', 'Low visual-platform presence'],
      accounts: [
        { platform: 'X', handle: '@omar_secops', url: 'https://x.com', followers: 27500, engagementRate: 2.7, score: 78, postsPerMonth: 34, latestPost: { text: 'New ransomware strain targeting OT networks in the region — patch your HMIs now.', date: '2026-10-27', likes: 920, comments: 141 } },
        { platform: 'LinkedIn', handle: 'omarhaddad-cyber', url: 'https://linkedin.com', followers: 17300, engagementRate: 3.0, score: 74, postsPerMonth: 4, latestPost: { text: 'What we learned running the National Cyber Drill: communication beats tooling.', date: '2026-10-02', likes: 610, comments: 52 } },
        { platform: 'YouTube', handle: '@HaddadCyberLabs', url: 'https://youtube.com', followers: 3900, engagementRate: 2.2, score: 49, postsPerMonth: 1, latestPost: { text: 'Incident response tabletop walkthrough', date: '2026-08-21', likes: 140, comments: 12 } },
      ],
    },
  },
];

export const expertiseFields: string[] = [
  'Artificial Intelligence Policy',
  'Digital Government',
  'Energy Transition',
  'Climate Policy',
  'Digital Health',
  'Financial Regulation',
  'Cultural Heritage',
  'Cybersecurity',
];
